"use client";

import { CloseSmall } from "@ellaandil/ui";
import { clsx } from "@trydig/ui";
import { type ComponentProps, useLayoutEffect, useRef, useState } from "react";
import { Typography } from "@/components/ui/Typography";
import { useSnackbar } from "@/features/navigation/Snackbar/hooks";

export interface SnackbarContentProps extends ComponentProps<"section"> {
	background: string;
	content: string | undefined;
}

/**
 * Renders the snackbar collapsed (`height: 0`, `opacity: 0`) on first paint so
 * SSR doesn't flash a banner that the user dismissed last session. After
 * hydration, `useSnackbar` reads localStorage; if this snackbar's hash isn't
 * in the dismissed set, we animate the wrapper's `height` from 0 to the
 * section's measured natural height (via ResizeObserver) and fade opacity in
 * over 300ms.
 *
 * Animating to a precise measured pixel height (vs. a hardcoded `max-h-20`
 * ceiling) keeps the easing curve consistent across the whole transition —
 * Safari in particular shows visible "stall" frames when `max-height` is set
 * larger than the content height, because the browser eases through the full
 * range even though no visual change happens past `content height`.
 *
 * The header below pushes down smoothly because it's next in document flow.
 */
export const SnackbarContent = ({
	background,
	content,
	className,
	...props
}: SnackbarContentProps) => {
	const { isVisible, dismiss } = useSnackbar({
		content,
		variant: background,
	});
	const isDark = background === "bg-brand-sand-100";

	const sectionRef = useRef<HTMLElement>(null);
	const [naturalHeight, setNaturalHeight] = useState(0);

	useLayoutEffect(() => {
		const el = sectionRef.current;
		if (!el) return;
		setNaturalHeight(el.offsetHeight);
		// Re-measure on font load, viewport resize, content swap, etc.
		const ro = new ResizeObserver(() => {
			setNaturalHeight(el.offsetHeight);
		});
		ro.observe(el);
		return () => {
			ro.disconnect();
		};
	}, []);

	return (
		<div
			aria-hidden={!isVisible}
			style={{ height: isVisible ? `${naturalHeight}px` : "0px" }}
			className="overflow-hidden transition-[height,opacity] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
		>
			<section
				ref={sectionRef}
				className={clsx(
					"grid items-center py-2 grid-cols-[1fr_auto_1fr] px-(--layout-page-inset) z-20 relative",
					background,
					isVisible ? "opacity-100" : "opacity-0",
					"transition-opacity duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
					className,
				)}
				{...props}
			>
				<Typography
					variant="body-sm-reg"
					className={clsx("col-start-2", isDark && "text-neutral-white-100")}
				>
					{content}
				</Typography>
				<CloseSmall
					height={24}
					width={24}
					className={clsx(
						"col-start-3 justify-self-end cursor-pointer hover:opacity-70",
						isDark && "text-neutral-white-100",
					)}
					onClick={dismiss}
				/>
			</section>
		</div>
	);
};
